import { Component, inject, OnInit } from '@angular/core'
import { eps } from '../../../services/url.service'
import { ActivatedRoute, Router } from '@angular/router'
import { NgIf, NgSwitch, NgSwitchCase } from '@angular/common'
import { CTCData, PayOutMode } from '../../../store/payout-agreement/payout-agreement.state'
import { User } from '../../../store/auth/auth.state'
import { select, Store } from '@ngrx/store'
import { loadUserById } from '../../../store/user/user.actions'
import { filter, firstValueFrom, take, tap } from 'rxjs'
import { selectSelectedUser } from '../../../store/user/user.reducer'
import { PayoutAgreementComponent } from '../payout-agreement/payout-agreement.component'
import { PayoutAgreementSummaryComponent } from '../payout-agreement-summary/payout-agreement-summary.component'
import {
  editPayout,
  payoutAgreementActions,
  payoutRevisionActions
} from '../../../store/payout-agreement/payout-agreement.actions'
import {
  selectPayout,
  selectRevisedPayout
} from '../../../store/payout-agreement/payout-agreement.reducers'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { loadSalaryComponents } from '../../../store/salary-components/salary-components.actions'
import { CUSTOM_DEDUCTIONS, CUSTOM_EARNINGS, REIMBURSEMENTS } from '../../../store/state.constants'
import { StatutoryCompliancesComponent } from '../statutory-compliances/statutory-compliances.component'
import { PayoutDataComponent } from '../payout-data/payout-data.component'
import { PayoutAgreementRevisionComponent } from '../payout-agreement-revision/payout-agreement-revision.component'
import { RevisedPayoutSummaryComponent } from '../revised-payout-summary/revised-payout-summary.component'

@Component({
  selector: 'app-payout-agreement-wrapper',
  imports: [
    NgIf,
    NgSwitch,
    NgSwitchCase,
    StatutoryCompliancesComponent,
    PayoutAgreementComponent,
    PayoutAgreementRevisionComponent,
    PayoutDataComponent,
    PayoutAgreementSummaryComponent,
    RevisedPayoutSummaryComponent
  ],
  templateUrl: './payout-agreement-wrapper.component.html',
  styleUrl: './payout-agreement-wrapper.component.scss'
})
export class PayoutAgreementWrapperComponent implements OnInit {
  private store = inject(Store)
  private router = inject(Router)
  private route = inject(ActivatedRoute)
  private modalService = inject(NgbModal)
  public currentMode!: PayOutMode
  public payoutMode = PayOutMode
  private employeeId = ''
  public employeeName = ''
  private belongsTo = ''
  private user!: User
  public screens = [0, 1, 2, 3]
  public currentScreenIndex = 0
  public nextButtonState = false

  async ngOnInit() {
    const employeeId = this.route.snapshot.paramMap.get('id')
    if (employeeId) this.employeeId = employeeId
    else this.router.navigate([eps.hrm.employeePath, this.employeeId])

    this.currentMode = this.route.snapshot.data['mode'] as PayOutMode

    this.store
      .select(selectPayout)
      .pipe(take(1))
      .subscribe(payout => {
        if (this.currentMode !== PayOutMode.CREATE && (payout?.ctc ?? 0) <= 0) {
          this.router.navigate([eps.hrm.employeePath, this.employeeId])
        }
      })

    this.store.dispatch(loadUserById({ id: this.employeeId }))
    // TODO: Load latest contractor payroll date
    //this.store.dispatch(
    //      userPayrollActions.loadLatestUserPayrollDate({ employeeId: this.employeeId })
    //    )
    await this.loadInitialUserDataAndDispatch()
  }

  async loadInitialUserDataAndDispatch(): Promise<void> {
    this.user = await firstValueFrom(
      this.store.pipe(
        select(selectSelectedUser),
        filter((user): user is User => !!user),
        take(1)
      )
    )

    if (this.user) {
      this.employeeName = this.user.firstName + ' ' + (this.user.lastName ?? '')
      this.belongsTo = this.user.business?.id ?? ''

      switch (this.currentMode) {
        case PayOutMode.CREATE:
          this.store.dispatch(
            payoutAgreementActions.addBusinessRequirements({
              belongsTo: this.belongsTo,
              employeeId: this.employeeId
            })
          )
          break
        case PayOutMode.REVISE:
        case PayOutMode.UPDATE:
          this.store.dispatch(
            payoutRevisionActions.initiatePayoutRevision({
              belongsTo: this.belongsTo,
              employeeId: this.employeeId
            })
          )
          break
        default:
          this.router.navigate([eps.hrm.employeePath, this.employeeId])
      }

      this.store.dispatch(
        loadSalaryComponents({ componentType: CUSTOM_EARNINGS, belongsTo: this.belongsTo })
      )
      this.store.dispatch(
        loadSalaryComponents({ componentType: CUSTOM_DEDUCTIONS, belongsTo: this.belongsTo })
      )
      this.store.dispatch(
        loadSalaryComponents({ componentType: REIMBURSEMENTS, belongsTo: this.belongsTo })
      )
    }
  }

  nextScreen() {
    // When screen[1] renders the default state of nextButtonState should be true
    //if (this.currentScreenIndex === 0) this.nextButtonState = true
    if (this.currentScreenIndex < this.screens.length - 1) {
      this.currentScreenIndex++
      return
    }
    switch (this.currentMode) {
      case PayOutMode.CREATE:
        this.store
          .pipe(
            select(selectPayout),
            filter(data => !!data),
            take(1),
            tap(payOut => {
              this.store.dispatch(
                payoutAgreementActions.addPayoutAgreement({ payoutAgreement: payOut })
              )
            })
          )
          .subscribe()
        break
      case PayOutMode.REVISE:
        this.store
          .pipe(
            select(selectRevisedPayout),
            filter(data => !!data),
            take(1),
            tap(payout => {
              this.store.dispatch(payoutRevisionActions.saveRevisedPayout({ payout }))
            })
          )
          .subscribe()
        break
      case PayOutMode.UPDATE:
        this.store
          .pipe(
            select(selectRevisedPayout),
            filter(data => !!data),
            take(1),
            tap(payout => {
              this.store.dispatch(editPayout({ payout }))
            })
          )
          .subscribe()
        break
      default:
        this.router.navigate([eps.hrm.employeePath, this.employeeId])
    }
    this.router.navigate([eps.hrm.employeePath, this.employeeId])
  }

  previousScreen() {
    this.nextButtonState = false
    if (this.currentScreenIndex > 0) this.currentScreenIndex--
    else {
      this.router.navigate([eps.hrm.employeePath, this.employeeId])
    }
  }

  // Created to reset the screen to navigate from summary screen to compliance screen
  resetScreen(screenNumber: number) {
    this.currentScreenIndex = screenNumber
  }

  setNextButtonState(state: boolean) {
    queueMicrotask(() => {
      this.nextButtonState = state
    })
  }

  onformValuesChanged(value: CTCData) {
    const ctcValueState = value.ctc !== 0 && value.ctc !== null
    const effectiveFromState = value.effectiveFrom !== '' && value.effectiveFrom !== null
    const payoutFromState = value.payoutFrom !== '' && value.payoutFrom !== null

    // Temporarily store the next button state so that changing it can be defered
    let newNextButtonState: boolean

    switch (this.currentMode) {
      case PayOutMode.CREATE:
        this.store.dispatch(payoutAgreementActions.updateCTCValue({ ctcValue: Number(value.ctc) }))
        newNextButtonState =
          this.currentScreenIndex === 0 || (ctcValueState && this.currentScreenIndex >= 1)
            ? false
            : true
        break
      case PayOutMode.REVISE:
        this.store.dispatch(payoutRevisionActions.updateCTCValue({ ctcValue: Number(value.ctc) }))
        newNextButtonState =
          this.currentScreenIndex === 0 ||
          (ctcValueState && effectiveFromState && payoutFromState && this.currentScreenIndex >= 1)
            ? false
            : true
        break
      case PayOutMode.UPDATE:
        this.store.dispatch(payoutRevisionActions.updateCTCValue({ ctcValue: Number(value.ctc) }))
        newNextButtonState =
          this.currentScreenIndex === 0 || (ctcValueState && this.currentScreenIndex >= 1)
            ? false
            : true
        break
      default:
        this.router.navigate([eps.hrm.employeePath, this.employeeId])
    }
    queueMicrotask(() => {
      this.nextButtonState = newNextButtonState
    })
  }

  //To preview the monthly salary
  openModal(modal: unknown) {
    const modalRef = this.modalService.open(modal, {
      scrollable: false,
      centered: true,
      size: 'lg'
    })
    //let employeeSalary!: MemoizedSelector<EmployeeSalaryState, EmployeeSalary>
    //if (this.currentMode === SalaryMode.CREATE) {
    //  employeeSalary = selectEmployeeSalary
    //} else {
    //  employeeSalary = selectRevisedSalary
    //}
    //this.store
    //  .select(employeeSalary)
    //  .pipe(
    //    take(1),
    //    tap(salary => {
    //      if (modalRef.componentInstance) {
    //        modalRef.componentInstance.employee = this.user
    //        modalRef.componentInstance.employeeSalaryDetails = salary
    //      }
    //    })
    //  )
    //  .subscribe()
  }
}
